/**
 * MT5 Bridge Health — admin operational view of the execution bridge.
 * Shows which users' EAs are online (heartbeat < 120s) and per-user
 * fill / reject / expire stats. Read-only. Auto-refreshes every 20s.
 */
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = {
  bg: "#0d1117", card: "#111722", border: "#1f2733", text: "#e6e7ec",
  muted: "#7f8792", gold: "#C9A227", green: "#22c55e", red: "#ef4444", blue: "#3b82f6",
};

function Stat({ label, value, color }) {
  return (
    <div style={{ background: C.card, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 16px", minWidth: 120 }}>
      <div style={{ fontSize: 11, color: C.muted, letterSpacing: 1, textTransform: "uppercase" }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color: color || C.text, fontFamily: "monospace" }}>{value}</div>
    </div>
  );
}

export default function Mt5BridgeHealth() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/mt5/admin/health`, { withCredentials: true });
      setData(data); setErr("");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Failed to load bridge health");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  if (loading) return <div style={{ padding: 24, color: C.muted }} data-testid="bridge-health-loading">Loading bridge health…</div>;
  if (err) return <div style={{ padding: 24, color: C.red }} data-testid="bridge-health-error">{err}</div>;

  const t = data.totals || {};
  return (
    <div style={{ padding: "8px 24px 40px" }} data-testid="mt5-bridge-health">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <h2 style={{ color: C.text, fontSize: 18, margin: 0 }}>MT5 Bridge Health</h2>
        <button onClick={load} data-testid="bridge-health-refresh" style={{ background: "transparent", border: `1px solid ${C.border}`, color: C.text, borderRadius: 8, padding: "7px 14px", fontSize: 12, cursor: "pointer" }}>↻ Refresh</button>
      </div>

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 22 }}>
        <Stat label="Connected users" value={data.total_connected_users} />
        <Stat label="EAs online now" value={data.online_now} color={data.online_now > 0 ? C.green : C.muted} />
        <Stat label="Filled" value={t.filled || 0} color={C.green} />
        <Stat label="Rejected" value={t.rejected || 0} color={C.red} />
        <Stat label="Expired" value={t.expired || 0} color={C.muted} />
        <Stat label="Open" value={t.open || 0} color={C.blue} />
      </div>

      {data.users.length === 0 ? (
        <div style={{ color: C.muted }} data-testid="bridge-health-empty">No users have connected an MT5 terminal yet.</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, color: C.text }} data-testid="bridge-health-table">
            <thead>
              <tr style={{ color: C.muted, textAlign: "left", fontSize: 11, textTransform: "uppercase", letterSpacing: 1 }}>
                {["User", "EA", "Account", "Mode", "Symbols", "Suffix", "Filled", "Rejected", "Expired", "Open", "Fill rate", "Last signal", "Last result", "Last seen"].map(h => (
                  <th key={h} style={{ padding: "8px 10px", borderBottom: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.users.map(u => (
                <tr key={u.user_id} data-testid={`bridge-user-${u.user_id}`} style={{ borderBottom: `1px solid ${C.border}` }}>
                  <td style={{ padding: "9px 10px" }}>
                    <div style={{ fontWeight: 600 }}>{u.name}</div>
                    {u.email && <div style={{ fontSize: 11, color: C.muted }}>{u.email}</div>}
                  </td>
                  <td style={{ padding: "9px 10px" }}>
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: u.connected ? C.green : C.muted, fontFamily: "monospace", fontSize: 12 }}>
                      <span style={{ width: 8, height: 8, borderRadius: "50%", background: u.connected ? C.green : C.muted }} />
                      {u.connected ? "ONLINE" : "OFFLINE"}
                    </span>
                  </td>
                  <td style={{ padding: "9px 10px", fontFamily: "monospace", fontSize: 12 }}>
                    {u.account ? `${u.account.login || "—"} · ${u.account.balance || "?"} ${u.account.currency || ""}` : <span style={{ color: C.muted }}>—</span>}
                  </td>
                  <td style={{ padding: "9px 10px", fontSize: 12 }}>
                    <span style={{ color: u.auto_execute ? C.green : C.muted }}>{u.auto_execute ? "AUTO" : "MANUAL"}</span>
                    {u.kill_switch && <span style={{ color: C.red, marginLeft: 6, fontWeight: 700 }}>KILL</span>}
                  </td>
                  <td style={{ padding: "9px 10px", fontSize: 11, fontFamily: "monospace", color: C.muted, whiteSpace: "nowrap" }}>
                    {(u.allowed_symbols && u.allowed_symbols.length) ? u.allowed_symbols.join(", ") : "—"}
                    {u.session_filter && u.session_filter.length ? <div style={{ color: C.gold, fontSize: 10 }}>⏱ {u.session_filter.join("/")}</div> : null}
                  </td>
                  <td style={{ padding: "9px 10px", fontFamily: "monospace", fontSize: 12, color: u.symbol_suffix ? C.text : C.muted }}>{u.symbol_suffix || "—"}</td>
                  <td style={{ padding: "9px 10px", color: C.green, fontFamily: "monospace" }}>{u.filled}</td>
                  <td style={{ padding: "9px 10px", color: u.rejected ? C.red : C.muted, fontFamily: "monospace" }}>{u.rejected}</td>
                  <td style={{ padding: "9px 10px", color: C.muted, fontFamily: "monospace" }}>{u.expired}</td>
                  <td style={{ padding: "9px 10px", color: C.blue, fontFamily: "monospace" }}>{u.open}</td>
                  <td style={{ padding: "9px 10px", fontFamily: "monospace" }}>{u.fill_rate == null ? "—" : `${u.fill_rate}%`}</td>
                  <td style={{ padding: "9px 10px", color: C.muted, fontSize: 11, whiteSpace: "nowrap" }}>
                    {u.last_signal_at ? <span>{u.last_signal_symbol || ""} {new Date(u.last_signal_at).toLocaleString()}</span> : "—"}
                  </td>
                  <td style={{ padding: "9px 10px", fontSize: 11, whiteSpace: "nowrap" }}>
                    {u.last_result
                      ? <span style={{ color: u.last_result === "filled" ? C.green : u.last_result === "rejected" ? C.red : C.muted, fontFamily: "monospace" }}>
                          {u.last_result}{u.last_reject_reason ? <span style={{ color: C.muted }}> · {u.last_reject_reason}</span> : ""}
                        </span>
                      : "—"}
                  </td>
                  <td style={{ padding: "9px 10px", color: C.muted, fontSize: 11, whiteSpace: "nowrap" }}>
                    {u.last_heartbeat_at ? new Date(u.last_heartbeat_at).toLocaleString() : "never"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
